// hh-mvm (CASE-35) — MoveContentsScreen.
//
// Альтернатива каскадному удалению, предлагаемая из DestructCascadeScreen:
// вместо того чтобы уничтожить собственность вместе со всем содержимым,
// пользователь переносит её помещения / вещи / оборудование в другую
// собственность, а опустевшая исходная удаляется. За экраном —
// POST /api/v1/properties/{id}/move-contents: одна транзакция, записанная как
// ChangeRecord kind="reparent", поэтому перенос целиком обратим через undo.

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { T, Icon, Btn, SectionLabel, entityIcon } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { properties as propertiesApi, humanizeError } from '../../api';
import type { PropertySummary } from '../../api/endpoints/properties.js';
import type { DestructPreviewCounts } from './DestructPreviewScreen';

export interface MoveContentsScreenProps {
  readonly router: RouterApi;
  /** Собственность-источник — её содержимое переедет, сама она удалится. */
  readonly sourcePropertyId: string;
  readonly sourceName: string;
  /** Сводка каскада из preview — что именно переедет. */
  readonly counts: DestructPreviewCounts;
  readonly onBack: () => void;
}

type Status = 'loading' | 'error' | 'ready';

function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

export function MoveContentsScreen({
  router,
  sourcePropertyId,
  sourceName,
  counts,
  onBack,
}: MoveContentsScreenProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [targets, setTargets] = useState<readonly PropertySummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  // Список собственности минус источник — туда переносить нельзя.
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    propertiesApi
      .listProperties()
      .then((list) => {
        if (cancelled) return;
        setTargets(list.filter((p) => p.id !== sourcePropertyId));
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(humanizeError(err, 'load'));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [sourcePropertyId]);

  const confirm = (): void => {
    if (selectedId === null || moving) return;
    setMoving(true);
    setMoveError(null);
    propertiesApi
      .moveContents(sourcePropertyId, { targetPropertyId: selectedId })
      .then(() => {
        // Источник удалён, содержимое переехало — возвращаемся в дерево.
        router.go('tree');
      })
      .catch((err: unknown) => {
        setMoveError(humanizeError(err, 'save'));
        setMoving(false);
      });
  };

  const summary = [
    counts.rooms > 0
      ? `${counts.rooms} ${pluralRu(counts.rooms, 'помещение', 'помещения', 'помещений')}`
      : null,
    counts.items > 0
      ? `${counts.items} ${pluralRu(counts.items, 'вещь', 'вещи', 'вещей')}`
      : null,
    counts.equipment > 0
      ? `${counts.equipment} ${pluralRu(counts.equipment, 'оборудование', 'единицы оборудования', 'единиц оборудования')}`
      : null,
  ].filter((s): s is string => s !== null);

  if (status === 'loading') {
    return (
      <div style={containerStyle}>
        <ScreenHeader title="Перенести содержимое" subtitle="Загружаю собственность…" onBack={onBack} />
        <div style={noteStyle}>Загружаю…</div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={containerStyle}>
        <ScreenHeader title="Не получилось" subtitle="Можно вернуться и попробовать заново" onBack={onBack} />
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          <div style={errorTextStyle}>{loadError}</div>
          <Btn kind="ghost" onClick={onBack} style={{ width: '100%' }}>
            Назад
          </Btn>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <ScreenHeader
        title="Куда перенести содержимое?"
        subtitle={`Из «${sourceName}» — сама собственность после этого удалится`}
        onBack={onBack}
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 16px' }}>
        <div style={summaryCardStyle}>
          <Icon name="folder" size={18} color={T.brandDeep} />
          <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.4 }}>
            {summary.length === 0
              ? 'В этой собственности пусто — переносить нечего, она просто удалится.'
              : `Переедет: ${summary.join(' · ')}. Ничего не потеряется — перенос можно откатить.`}
          </div>
        </div>

        {targets.length === 0 ? (
          <div style={{ marginTop: 16 }}>
            <div style={errorTextStyle}>
              Другой собственности нет. Создайте её в дереве или вернитесь и удалите эту вместе
              с содержимым.
            </div>
          </div>
        ) : (
          <>
            <SectionLabel style={{ padding: '16px 4px 6px' }}>Целевая собственность</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {targets.map((p) => {
                const active = p.id === selectedId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={moving}
                    onClick={() => {
                      setSelectedId(p.id);
                      setMoveError(null);
                    }}
                    style={{
                      padding: '12px',
                      background: active ? T.brandSoft : T.surface,
                      border: `1px solid ${active ? T.brandSoft2 : T.border}`,
                      borderRadius: 12,
                      fontFamily: T.font,
                      cursor: moving ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      textAlign: 'left',
                    }}
                  >
                    <Icon
                      name={entityIcon('property', { name: p.name })}
                      size={18}
                      color={active ? T.brandDeep : T.ink3}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 650,
                          color: active ? T.brandDeep : T.ink,
                        }}
                      >
                        {p.name}
                      </div>
                    </div>
                    {active && <Icon name="check" size={16} color={T.brandDeep} />}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {moveError !== null && <div style={{ ...errorTextStyle, marginTop: 14 }}>{moveError}</div>}
      </div>

      {targets.length > 0 && (
        <div style={footerStyle}>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn kind="ghost" onClick={onBack} disabled={moving} style={{ flex: 1 }}>
              Назад
            </Btn>
            <Btn
              kind="primary"
              icon="check"
              onClick={confirm}
              disabled={selectedId === null || moving}
              style={{ flex: 1.4 }}
            >
              {moving ? 'Переношу…' : 'Перенести сюда'}
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}

const containerStyle: JSX.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  background: T.bg,
};

const noteStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  fontSize: 14,
  fontWeight: 500,
  color: T.ink3,
};

const errorTextStyle: JSX.CSSProperties = {
  fontSize: 14,
  color: T.danger,
  fontWeight: 500,
  marginBottom: 16,
  lineHeight: 1.4,
};

const summaryCardStyle: JSX.CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  padding: 14,
  marginTop: 14,
  background: T.brandSoft,
  border: `1px solid ${T.brandSoft2}`,
  borderRadius: 14,
};

const footerStyle: JSX.CSSProperties = {
  padding: '10px 16px 16px',
  borderTop: `1px solid ${T.border}`,
  background: T.bg,
};
